import { PostEntityCreationRuleApi } from '@portal/pkg-api/admin'
import { data } from '@portal/pkg-api/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  EntityTemplate,
  FilterRestriction,
  PostEntityCreationRuleRequest,
} from '@portal/pkg-api/gen/admin'
import { EntityType } from '@portal/pkg-api/gen/navigator'
import { ZonedDateTime } from '@js-joda/core'

const prefix = 'admin.postEntityCreationRules'

export const PostEntityCreationRuleQueries = {
  rules: (entityTypeId: number) => ({
    queryKey: [`${prefix}.rules`, entityTypeId],
    queryFn: async (): Promise<ReadonlyArray<PostEntityCreationRuleBasic>> => {
      const response = await data(
        PostEntityCreationRuleApi.getPostEntityCreationRules(entityTypeId),
      )
      return response.map(({ entityType, createdDate, ...r }) => ({
        ...r,
        entityTypeId: entityType,
        createdDate: ZonedDateTime.parse(createdDate),
      }))
    },
  }),
  rule: (postEntityCreationRuleId: number) => ({
    queryKey: [`${prefix}.rule`, postEntityCreationRuleId],
    queryFn: async (): Promise<PostEntityCreationRuleDetail> => {
      const { entityType, createdDate, ...response } = await data(
        PostEntityCreationRuleApi.getPostEntityCreationRule(postEntityCreationRuleId),
      )
      return {
        ...response,
        entityTypeId: entityType,
        createdDate: ZonedDateTime.parse(createdDate),
      }
    },
  }),
  targetEntityTypes: (entityTypeId: number) => ({
    queryKey: [`${prefix}.targetEntityTypes`, entityTypeId],
    queryFn: async (): Promise<ReadonlyArray<EntityType>> => {
      return await data(PostEntityCreationRuleApi.getTargetEntityTypes(entityTypeId))
    },
  }),
  // Authorized by rule administration rather than the caller's Entity Type scope, so any target
  // the picker offers can be loaded. Do not swap for the navigator's entity type lookup.
  targetEntityType: (targetEntityTypeId?: number) => ({
    queryKey: [`${prefix}.targetEntityType`, targetEntityTypeId],
    queryFn: async (): Promise<EntityType> => {
      return await data(PostEntityCreationRuleApi.getTargetEntityType(targetEntityTypeId!))
    },
    enabled: !!targetEntityTypeId,
  }),
}

export const useCreatePostEntityCreationRule = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: PostEntityCreationRuleRequest) => {
      return await data(PostEntityCreationRuleApi.createPostEntityCreationRule(req))
    },
    onSuccess: (response) => {
      void queryClient.invalidateQueries(PostEntityCreationRuleQueries.rules(response.entityType))
      void queryClient.invalidateQueries(PostEntityCreationRuleQueries.rule(response.id))
    },
  })
}

export const useUpdatePostEntityCreationRule = (postEntityCreationRuleId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: PostEntityCreationRuleRequest) => {
      return await data(
        PostEntityCreationRuleApi.updatePostEntityCreationRule(postEntityCreationRuleId, req),
      )
    },
    onSuccess: (response) => {
      void queryClient.invalidateQueries(PostEntityCreationRuleQueries.rules(response.entityType))
      void queryClient.invalidateQueries(PostEntityCreationRuleQueries.rule(response.id))
    },
  })
}

export const useCreateUpdatePostEntityCreationRule = (ruleId?: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (req: PostEntityCreationRuleRequest) => {
      if (ruleId) {
        return await data(PostEntityCreationRuleApi.updatePostEntityCreationRule(ruleId, req))
      } else {
        return await data(PostEntityCreationRuleApi.createPostEntityCreationRule(req))
      }
    },
    onSuccess: (response) => {
      void queryClient.invalidateQueries(PostEntityCreationRuleQueries.rules(response.entityType))
      void queryClient.invalidateQueries(PostEntityCreationRuleQueries.rule(response.id))
    },
  })
}

export const useDeletePostEntityCreationRule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (postEntityCreationRuleId: number) => {
      await PostEntityCreationRuleApi.deletePostEntityCreationRule(postEntityCreationRuleId)
    },
    onSuccess: (_, postEntityCreationRuleId) => {
      // Invalidate all rules list queries to refresh the grid
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === `${prefix}.rules` ||
          query.queryKey.includes(postEntityCreationRuleId) ||
          query.queryKey.includes(PostEntityCreationRuleQueries.rule(postEntityCreationRuleId)),
      })
    },
  })
}

export interface PostEntityCreationRuleBasic {
  id: number
  entityTypeId: number
  name: string
  targetEntityType: number
  triggerAction: string
  createdBy: number
  createdDate: ZonedDateTime
  deletable: boolean
}

export interface PostEntityCreationRuleDetail extends PostEntityCreationRuleBasic {
  name: string
  filterRestrictions: ReadonlyArray<FilterRestriction>
  template: EntityTemplate
  initialStageTransitionId?: string
}
